import UniversalSearch from "../universal-search";
import { FEATURE_ROUTES } from "../../config/navigation";
// Import package components
import { Fragment, useContext, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import {
  Image,
  Navbar,
  NavbarContainer,
  NavbarDropdown,
  NavItem,
} from "uikit-react";
import UIkit from "uikit";
import moment from "moment";
import { Link as RawLink } from "../ui-kit/Link";
import { AppContexts } from "../../providers";
import feather from "feather-icons";

import Notification from "./notification";
import TrialAccount from "../common/TrialAccount";
import { usePermissionsStore } from "../permissions/permissions_store";

// Helper component to render Feather icons
const FeatherIcon = ({ name, className = "" }) => {
  const icon = feather.icons[name];
  if (!icon) return null;
  
  return (
    <svg
      className={`feather-icon ${className}`}
      viewBox="0 0 24 24"
      dangerouslySetInnerHTML={{ __html: icon.contents }}
      style={{ color: "var(--dark-purple)" }}
    />
  );
};

const Link = ({ onClick, ...props }) => (
  <RawLink
    onClick={() => {
      onClick && onClick();
    }}
    {...props}
  />
);

const Header = () => {
  const { user: account, inbox, logout } = useContext(AppContexts.AuthContext);
  const { newOrderCount, getNewOrderCount } = useContext(
    AppContexts.OrdersContext,
  );
  const { canView } = usePermissionsStore();

  const location = useLocation();
  const hasActiveTrial =
    ["trial", "trial-full"].includes(account?.details?.type) &&
    moment(account?.details?.trial_end).isAfter(moment());

  //const history = useHistory();

  const handleLogout = async () => {
    console.log("logging out");

    logout()
      .then(() => {
        console.log("Goodbye");
      })
      .catch((err) => {
        console.warn("Error logging out:", err);
      });
  };

  const profileImage = useMemo(() => {
    return (
      (account?.user?.attachments || []).find((a) => a.type === "image") || null
    );
  }, [account]);

  useEffect(() => {
    getNewOrderCount();
  }, []);

  const isActive = (paths) => {
    if (!Array.isArray(paths)) {
      paths = [paths];
    }
    return paths.some((path) => {
      const regex = new RegExp(`^${path}`);
      return regex.test(location.pathname);
    })
      ? "uk-active"
      : "";
  };

  const hasExtras = () => {
    return (account?.details?.product || "").split(",").includes("extras");
  };

  const hasFlag = (flagName) => {
    const accountFlags = account?.flags || [];
    return accountFlags.some((f) => f.name === flagName);
  };

  const adminMenuGroups = [
    [{ label: "Company Settings", to: "/settings", module: "admin.settings" }],
    [
      { label: "Units", to: "/units", module: "admin.units" },
      { label: "Macros", to: "/macros", module: "admin.macros" },
      { label: "Custom Fields", to: "/custom-fields", module: "crm.custom_fields", enabled: hasFlag("crm") },
    ],
    [
      { label: "AI Chatbot FAQs", to: "/chatbot-faqs", module: "messaging.chatbot" },
      { label: "AI Chatbot Unanswered Questions", to: "/unanswered-questions", module: "messaging.chatbot" },
    ],
    [
      { label: "Users", to: "/team", module: "admin.users" },
      { label: "Roles & Permissions", to: "/roles", module: "admin.roles" },
      { label: "Zones", to: "/zones", module: "admin.zones" },
    ],
    [
      { label: "Billing", to: "/billing", module: "admin.billing" },
      { label: "Payments", to: "/payments", module: "guides.payments", enabled: hasExtras() },
    ],
  ].map((group) => group.filter((item) => item.enabled !== false && canView(item.module)))
    .filter((group) => group.length > 0);
  const canAccessAdminMenu = adminMenuGroups.length > 0;

  // const isOnboardingUser = () => {
  //   return account && account?.details?.type === 'onboarding';
  // }

  return (
    <>
      <div
        id="ruebarue-navbar"
        className={`rbr-header-sticky${
          hasActiveTrial ? " has-trial-account" : ""
        }`}
      >
        <NavbarContainer className="hms-navbar">
          <div className="rbr-header-content">
            <TrialAccount />
          <div
            className="navbar-collapse"
            id="navbar_collapse"
          >
            {!!account && (
              <Navbar right>
                  <li className="nav-search-item"><UniversalSearch /></li>
                {account?.details?.type === "onboarding" && (
                  <NavItem className={isActive(["/getting-started"])}>
                    <Link
                      to="/getting-started"
                      uk-tooltip="Get Started"
                      rel="noopener noreferrer"
                    >
                      <Image
                        src="/images/getting-started.svg"
                        className="margin-zero"
                      />
                    </Link>
                  </NavItem>
                )}
                {canAccessAdminMenu && (
                  <NavItem className={`nav-admin-item ${isActive([
                    "/team",
                    "/roles",
                    "/zones",
                    "/settings",
                    "/billing",
                    "/payments",
                    "/units",
                    "/macros",
                    ...(hasFlag("crm") ? ["/custom-fields"] : []),
                    "/chatbot-faqs",
                    "/unanswered-questions",
                  ])}`}>
                    <Link to="#" uk-tooltip="Admin" aria-label="Admin">
                      <span className="nav-admin-gear-icon">
                        <FeatherIcon name="settings" />
                      </span>
                    </Link>
                    <NavbarDropdown id="admin-dropdown">
                      {adminMenuGroups.map((group, index) => (
                        <Fragment key={group[0].to}>
                          {index > 0 && <li className="uk-nav-divider" />}
                          {group.map((item) => (
                            <NavItem key={item.to}><Link to={item.to}>{item.label}</Link></NavItem>
                          ))}
                        </Fragment>
                      ))}
                    </NavbarDropdown>
                  </NavItem>
                )}
                <NavItem className={`nav-help-item ${isActive(["/whats-new", "/features"])}`}>
                  <Link to="#" uk-tooltip="Help" aria-label="Help">
                    <span className="nav-help-icon">
                      <FeatherIcon name="help-circle" />
                    </span>
                  </Link>
                  <NavbarDropdown id="help-dropdown">
                    <NavItem>
                      <Link to={FEATURE_ROUTES.help} onClick={() => UIkit.dropdown("#help-dropdown").hide(0)}>
                        Help Center
                      </Link>
                    </NavItem>
                    <NavItem>
                      <Link to={FEATURE_ROUTES.discovery} onClick={() => UIkit.dropdown("#help-dropdown").hide(0)}>
                        Your Features
                      </Link>
                    </NavItem>
                    <NavItem>
                      <Link
                        onClick={() => {
                          UIkit.dropdown("#help-dropdown").hide(0);
                        }}
                        to={FEATURE_ROUTES.news}
                      >
                        What's New
                      </Link>
                    </NavItem>
                  </NavbarDropdown>
                </NavItem>
                <NavItem className={`nav-profile-item ${isActive(["/profile"])}`}>
                  <Link to="#">
                    <img
                      src={profileImage?.url || "/images/profile.svg"}
                      className="nav-profile-avatar"
                      alt="Profile"
                    />
                  </Link>
                  <NavbarDropdown id="profile-dropdown">
                    <NavItem>
                      <Link
                        onClick={() => {
                          UIkit.dropdown("#profile-dropdown").hide(0);
                        }}
                        to={FEATURE_ROUTES.profile}
                      >
                        Profile
                      </Link>
                    </NavItem>
                    <NavItem>
                      <Link to="#" onClick={handleLogout}>
                        Sign Out
                      </Link>
                    </NavItem>
                  </NavbarDropdown>
                  </NavItem>
                </Navbar>
              )}
            </div>
          </div>
        </NavbarContainer>
      </div>
      <Notification />
    </>
  );
};

export default Header;
